import React, { useState } from "react";
import { Key, Clock, FileText, Link2, Copy, Check, MessageCircle, ShieldCheck, AlertTriangle } from "lucide-react";
import DangerousFileLink, { isDangerousUrl } from "./DangerousFileLink";

const absUrl = (u) => u;  // same-origin, no prefix needed

/**
 * Order detail card that shows the software access bundle (link/file, license
 * key, duration, instructions). Visible only to the buyer company and the
 * assigned creator — the backend already scrubs `software` for everyone else.
 */
export const SoftwareAccessCard = ({ order, viewerRole, onUpdateClick }) => {
  const sw = order?.software;
  const [copied, setCopied] = useState(false);
  const verified = !!order?.company_verified;

  if (!sw || sw.method === "later") {
    return (
      <section data-testid="software-access-card" className="cc-surface rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <Key size={14} className="text-[#00ff88]" />
          <div className="cc-overline">Software access</div>
        </div>
        <div className="rounded-lg border border-dashed border-[#15152a] bg-[#06060c] p-4 text-sm text-[#a0a0b0]">
          <div className="inline-flex items-center gap-2 text-[#ffaa00] mb-1">
            <MessageCircle size={14} />
            <span className="text-white font-medium">Not shared yet</span>
          </div>
          <p className="text-[12px] leading-relaxed">
            The buyer chose to send software access via chat. Open a conversation to receive
            the loader/installer, license key and setup instructions.
          </p>
          {onUpdateClick && (
            <button
              type="button"
              data-testid="software-attach-now-btn"
              onClick={onUpdateClick}
              className="mt-3 text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1"
            >
              Attach software now →
            </button>
          )}
        </div>
      </section>
    );
  }

  const copyKey = async () => {
    if (!sw.license_key) return;
    try {
      await navigator.clipboard.writeText(sw.license_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  };

  const url = sw.url;
  const dangerous = isDangerousUrl(url || "");

  return (
    <section data-testid="software-access-card" className="cc-surface rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Key size={14} className="text-[#00ff88]" />
        <div className="cc-overline">Software access</div>
        <span className={`ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${verified ? "text-[#ffaa00]" : "text-[#ff4444]"}`}>
          {verified ? (<><ShieldCheck size={10} /> Verified company</>) : (<><AlertTriangle size={10} /> Unverified</>)}
        </span>
      </div>

      {url && (
        <div>
          <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
            <Link2 size={11} /> {sw.method === "file" ? "Software file" : "Download link"}
          </div>
          {dangerous ? (
            <DangerousFileLink url={url} verified={verified} testId="software-download-link" fullWidth />
          ) : (
            <a
              href={absUrl(url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="software-external-link"
              className="block w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-[#00ff88] font-mono truncate hover:border-[#1f1f3a]"
            >
              {url}
            </a>
          )}
        </div>
      )}

      {(sw.license_key || sw.license_duration) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {sw.license_key && (
            <div>
              <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
                <Key size={11} /> License key
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="software-key-display"
                  value={sw.license_key}
                  readOnly
                  className="flex-1 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring tracking-wider"
                />
                <button
                  type="button"
                  onClick={copyKey}
                  data-testid="software-key-copy-btn"
                  className="px-3 py-2 rounded-lg border border-[#15152a] bg-[#06060c] text-[#00ff88] hover:border-[#00ff88]/40 transition-colors"
                  aria-label="Copy license key"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          )}
          {sw.license_duration && (
            <div>
              <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
                <Clock size={11} /> Duration
              </div>
              <div data-testid="software-duration-display" className="bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono">
                {sw.license_duration}
              </div>
            </div>
          )}
        </div>
      )}

      {sw.instructions && (
        <div>
          <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
            <FileText size={11} /> Setup instructions
          </div>
          <div
            data-testid="software-instructions-display"
            className="rounded-lg border border-[#15152a] bg-[#06060c] p-3 text-sm text-[#a0a0b0] whitespace-pre-wrap leading-relaxed"
          >
            {sw.instructions}
          </div>
        </div>
      )}

      <p className="text-[11px] text-[#606070] leading-relaxed">
        Visible only to you and the {viewerRole === "creator" ? "buyer" : "creator"}.
        Never share this key publicly — the buyer can revoke it any time.
      </p>
    </section>
  );
};

export default SoftwareAccessCard;
